import { create } from 'zustand';
import { type Account } from '@shared/types/entities/account';
import { IpcChannel } from '@shared/types/ipc';
import { isElectron } from '@shared/utils/is-electron';
import { useApiServiceStore } from './api';

type Status = 'idle' | 'fetching' | 'submitting' | 'error';

type AddressBookStore = {
  isInitialized: boolean;
  init: () => void;
  cleanUp: () => void;
  status: Status;
  accountsEWC: Account[];
  accountsETH: Account[];
  errorMsg: string | undefined;
  fetchAccounts: () => Promise<void>;
  updateAccounts: () => Promise<void>;
  addAccount: (address: string, network: string) => Promise<void>;
  deleteAccount: (address: string, network: string) => Promise<void>;
  resetErrorMsg: () => void;
};

export const useAddressBookStore = create<AddressBookStore>((set, get) => ({
  isInitialized: false,
  init: () => {
    if (get().isInitialized) return;
    set({ isInitialized: true });

    if (isElectron()) {
      window.Api.on(IpcChannel.addressBalanceUpdated, () => {
        get().updateAccounts();
      });
    }
  },
  cleanUp: () => {
    if (isElectron()) {
      window.Api.removeAllListeners(IpcChannel.addressBalanceUpdated);
    }
    set({ isInitialized: false });
  },
  status: 'idle',
  accountsEWC: [],
  accountsETH: [],
  errorMsg: undefined,
  // This function is abstracted to update accounts optimistically without updating the status
  updateAccounts: async () => {
    const workerApi = useApiServiceStore.getState().api;

    // Fetch accounts by network separately for better performance
    const [ewcDbAccounts, ethDbAccounts] = await Promise.all([
      workerApi.addressBookList('EWC'),
      workerApi.addressBookList('ETH'),
    ]);

    const ewcAccounts = ewcDbAccounts.map((account, index) => ({
      name: `Account ${index + 1}`,
      address: account.address,
      balance: parseFloat(account.balance),
    }));

    const ethAccounts = ethDbAccounts.map((account, index) => ({
      name: `Account ${index + 1}`,
      address: account.address,
      balance: parseFloat(account.balance),
    }));

    set({ accountsEWC: ewcAccounts, accountsETH: ethAccounts });
  },
  fetchAccounts: async () => {
    try {
      set({ status: 'fetching', errorMsg: undefined });
      await get().updateAccounts();
      set({ status: 'idle' });
    } catch (error) {
      set({ status: 'error', errorMsg: 'There was an error loading EWC accounts' });
    }
  },
  addAccount: async (address: string, network: string) => {
    set({ status: 'submitting', errorMsg: undefined });
    try {
      const workerApi = useApiServiceStore.getState().api;
      const result = await workerApi.addressBookCreate(address, network);

      if (result.status === 'ok') {
        await get().updateAccounts();
        set({ status: 'idle' });
        return;
      }

      set({ status: 'error', errorMsg: result.message });
    } catch (error) {
      set({ status: 'error', errorMsg: 'There was an error adding the account' });
    }
  },
  deleteAccount: async (address, network) => {
    try {
      const workerApi = useApiServiceStore.getState().api;
      set({ status: 'submitting', errorMsg: undefined });
      await workerApi.addressBookDelete(address, network);
      await get().updateAccounts();
      set({ status: 'idle' });
    } catch (error) {
      set({ status: 'error', errorMsg: 'There was an error deleting the account' });
    }
  },
  resetErrorMsg: () => set({ errorMsg: undefined }),
}));
